import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  MobileDrawer,
  Popover,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import moment from 'moment';
import PropTypes from 'prop-types';

import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import { getAuditHistoryData } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  SANITIZED_DATE_FORMATS_MAPPING,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import {
  getCertKind,
  getEOCertKind,
  getEOFixedColKey,
  getFixedColKey,
} from '../../CertsDetailsList/Tile/utils';
import { CERT_ENTITLEMENT_VALUEKEY } from '../constant';

import CertsConsultedContent from './CertsConsultedContent';
import { MANDATORY_COLUMNS_MSG_KEY } from './constants';
import { invertedMessages } from './messages';

import './CertsConsultedActionPopover.css';

function CertsConsultedActionPopover({
  isMobileXlargeWidth,
  isCommentPopoverOpen,
  setIsCommentPopoverOpen,
  placement,
  trigger,
  onClose,
  resetAction,
  nodeData,
  certKey,
  globalDateFormat,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { messages } = useSelector((state) => state?.user);
  const { campaignType } = useSelector(
    (state) => state?.certifications?.details?.core
  );
  const l = useInvertedLocalize(invertedMessages);

  const [isLoadingActionDetails, setIsLoadingActionDetails] = useState(true);
  const [latestActionDetails, setLatestActionDetails] = useState(null);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const certsKind = isEOCampaign
    ? getEOCertKind(nodeData)
    : getCertKind(nodeData);
  const fixedColKey = isEOCampaign
    ? getEOFixedColKey(certsKind)
    : getFixedColKey(certsKind);

  const isNoValueTitle =
    nodeData[fixedColKey] === null ||
    nodeData[fixedColKey] === undefined ||
    nodeData?.[fixedColKey]?.trim()?.length === 0;
  const value = isNoValueTitle ? 'Not Provided' : nodeData[fixedColKey];

  const heading =
    messages[MANDATORY_COLUMNS_MSG_KEY[`${fixedColKey}.ATTESTATIONS`]] ||
    localize(
      intl,
      getMessageKey(MANDATORY_COLUMNS_MSG_KEY[`${fixedColKey}.ATTESTATIONS`])
    );

  const fetchAuditHistory = async () => {
    const body = {
      certkey: certKey,
      cert_entitlement_valuekey: nodeData?.CERT_ENTITLEMENT_VALUEKEY,
      cert_userkey: nodeData?.CERT_USERKEY,
    };

    if (isEOCampaign) {
      if (nodeData?.CERT_ACCOUNTKEY) {
        body.cert_accountkey = nodeData.CERT_ACCOUNTKEY;
      }

      if (nodeData?.CERT_ROLEKEY) {
        body.cert_rolekey = nodeData.CERT_ROLEKEY;
      }

      if (nodeData?.CERT_ENTITLEMENT_VALUE2KEY) {
        body.certEntVal2Key = nodeData.CERT_ENTITLEMENT_VALUE2KEY.toString();
      }
    } else {
      switch (nodeData?.CERT_ENTITLEMENT_VALUEKEY) {
        case CERT_ENTITLEMENT_VALUEKEY.USER:
          break;
        case CERT_ENTITLEMENT_VALUEKEY.ACCOUNT:
          if (nodeData.CERT_ACCOUNTKEY) {
            body.cert_accountkey = nodeData.CERT_ACCOUNTKEY;
          }

          break;
        case CERT_ENTITLEMENT_VALUEKEY.ROLE:
          body.cert_rolekey = nodeData.CERT_ROLEKEY;

          break;
        case CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT:
        default:
          if (CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT >= 0) {
            if (nodeData.CERT_ACCOUNTKEY) {
              body.cert_accountkey = nodeData.CERT_ACCOUNTKEY;
            }

            if (nodeData?.CERT_ROLEKEY) {
              body.cert_rolekey = nodeData.CERT_ROLEKEY;
            }
          }

          break;
      }
    }

    dispatch(getAuditHistoryData(body))
      .then((data) => {
        const { response } = data;

        if (Array.isArray(response) && response.length > 0) {
          let firstNonConsultedAction = null;

          response.forEach((ele) => {
            if (ele.action !== 'consulted' && !firstNonConsultedAction) {
              firstNonConsultedAction = ele;
            }
          });

          // storing non consulted action
          if (firstNonConsultedAction) {
            setLatestActionDetails(firstNonConsultedAction);
          }
        } else {
          const details = {
            errorMsg: l('Network Error , Please retry after sometime'),
          };

          setLatestActionDetails(details);
        }
      })
      .catch(() => {
        const details = {
          errorMsg: l('Network Error , Please retry after sometime'),
        };

        setLatestActionDetails(details);
      })
      .finally(() => {
        setIsLoadingActionDetails(false);
      });
  };

  useEffect(() => {
    if (isCommentPopoverOpen) {
      fetchAuditHistory();

      return;
    }

    setIsLoadingActionDetails(true);
  }, [isCommentPopoverOpen]);

  const handleClose = () => setIsCommentPopoverOpen(false);

  const handleSubmit = () => {
    resetAction();
    handleClose();
  };

  const getActionType = (type) => {
    if (type !== '') return type;

    return 'NOT_PROVIDED';
  };

  const getIconKind = () => {
    switch (latestActionDetails?.action) {
      case 'reassigned':
        return 'ReassignUser';
      case 'consulted':
      case 'conditionallyCertified':
        return 'Consult';
      case 'rejected':
        return 'Reject';
      case 'approved':
      case 'cancelled':
        return 'CheckCircleOutline';
      case 'worksForMe':
      default:
        return 'Success';
    }
  };

  const getIconColor = () => {
    switch (latestActionDetails?.action) {
      case 'reassigned':
        return 'navy-900';
      case 'consulted':
        return 'green-900';
      case 'conditionallyCertified':
        return 'success-900';
      case 'rejected':
        return 'warning-900';
      case 'approved':
        return 'success-900';
      case 'worksForMe':
        return 'success-700';
      case 'cancelled':
        return 'warning-700';
      default:
        return 'success-700';
    }
  };

  const renderActionResetContent = () => {
    if (isLoadingActionDetails) {
      // Skeleton added
      return (
        <>
          <Box className='CertsConsultedActionPopover-icon'>
            <Icon kind={certsKind} />
            <Box className='CertsConsultedActionPopover-text'>
              <Typography kind='h4' mobile='h4-d'>
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                {`${heading}: ${value}`}
              </Typography>
            </Box>
          </Box>
          <Box className='CertsConsultedActionPopover-headerWrapper'>
            <Box className='CertsConsultedActionPopover-title'>
              <Icon
                kind={getIconKind()}
                color={getIconColor()}
                className='CertsConsultedActionPopover-headerIcon'
              />
              <div className='CertsConsultedActionPopoverSkeleton-title CertsConsultedActionPopoverSkeleton-animateSkeleton' />
            </Box>
          </Box>
          <Box kind='body1' className='CertsConsultedActionPopover-details'>
            <div className='CertsConsultedActionPopoverSkeleton'>
              <div className='CertsConsultedActionPopoverSkeleton-heading CertsConsultedActionPopoverSkeleton-animateSkeleton' />
              <div className='CertsConsultedActionPopoverSkeleton-info CertsConsultedActionPopoverSkeleton-animateSkeleton' />
            </div>
            <div className='CertsConsultedActionPopoverSkeleton'>
              <div className='CertsConsultedActionPopoverSkeleton-heading CertsConsultedActionPopoverSkeleton-animateSkeleton' />
              <div className='CertsConsultedActionPopoverSkeleton-info CertsConsultedActionPopoverSkeleton-animateSkeleton' />
            </div>
          </Box>
          <Box className='CertsConsultedActionPopover-commentsInfo'>
            <div className='CertsConsultedActionPopoverSkeleton'>
              <div className='CertsConsultedActionPopoverSkeleton-heading CertsConsultedActionPopoverSkeleton-animateSkeleton' />
              <div className='CertsConsultedActionPopoverSkeleton-info CertsConsultedActionPopoverSkeleton-animateSkeleton' />
            </div>
          </Box>
        </>
      );
    }

    if (latestActionDetails.errorMsg) {
      return (
        <Box className='CertsConsultedActionPopover-errorMsg'>
          <Typography kind='body2-bold'>
            {latestActionDetails?.errorMsg}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box className='CertsConsultedActionPopover-icon'>
          <Icon kind={certsKind} />
          <Box className='CertsConsultedActionPopover-text'>
            <Typography kind='h4' mobile='h4-d'>
              {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
              {`${heading}: ${value}`}
            </Typography>
          </Box>
        </Box>
        {/* Mobile header */}
        <Box className='CertsConsultedActionPopover-headerWrapper'>
          <Box className='CertsConsultedActionPopover-title'>
            <Icon
              kind={getIconKind()}
              color={getIconColor()}
              className='CertsConsultedActionPopover-headerIcon'
            />
            <Typography kind='label' desktop='h3-d' mobile='h3-d' lineLimit={2}>
              {getActionType(latestActionDetails?.actionMessage)}
            </Typography>
          </Box>
        </Box>
        {/* Mobile content */}
        <Box kind='body1' className='CertsConsultedActionPopover-details'>
          <Box className='CertsConsultedActionPopover-detailsSection'>
            <Typography
              kind='body3'
              className='CertsConsultedActionPopover-heading'>
              {l('Actioned By')}
            </Typography>
            <Typography
              kind='body2'
              color='--color-foreground-secondary-bold'
              lineLimit={2}>
              {latestActionDetails?.actionUser}
            </Typography>
          </Box>
          <Box>
            <Typography
              kind='body3'
              className='CertsConsultedActionPopover-heading'>
              {l('Actioned On')}
            </Typography>
            <Typography
              kind='body2'
              color='--color-foreground-secondary-bold'
              lineLimit={2}>
              {latestActionDetails?.actionDate &&
                moment
                  .utc(
                    latestActionDetails?.actionDate,
                    SANITIZED_DATE_FORMATS_MAPPING?.[
                      latestActionDetails?.actionDateFormat
                    ]
                  )
                  .local()
                  .format(`${globalDateFormat?.toUpperCase()} HH:mm:ss`)}
            </Typography>
          </Box>
        </Box>
        <Box className='CertsConsultedActionPopover-commentsInfo'>
          <Typography
            kind='body3'
            className='CertsConsultedActionPopover-heading'>
            {l('Comments')}
          </Typography>
          <Tooltip
            openDelay={500}
            text={
              latestActionDetails?.comment?.length > 100
                ? latestActionDetails?.comment
                : null
            }
            maxWidth='20rem'
            placement='bottom'
            offset={8}
            trigger={
              <Typography
                kind='body2'
                color='--color-foreground-secondary-bold'
                lineLimit={2}>
                {latestActionDetails?.comment || '-'}
              </Typography>
            }
          />
        </Box>
      </>
    );
  };

  return (
    <Box tag='div' className='CertsConsultedActionPopover-contextWrapper'>
      {isMobileXlargeWidth ? (
        <>
          {trigger}
          <MobileDrawer
            isSwipeEnabled
            isOpen={isCommentPopoverOpen}
            setIsOpen={setIsCommentPopoverOpen}
            content={renderActionResetContent()}
            footerActionBar={
              <FooterActionBar
                size='large'
                className='CertsConsultedActionPopover-controls'>
                <Button
                  type='button'
                  kind='subtle'
                  onClick={handleSubmit}
                  className='CertsConsultedActionPopover-cancelBtn'>
                  {l('Cancel Action')}
                </Button>
                <Button
                  type='button'
                  kind='filled'
                  onClick={handleClose}
                  className='CertsConsultedActionPopover-submtBtn'>
                  {l('Got it')}
                </Button>
              </FooterActionBar>
            }
            className='CertsConsultedActionPopover'
            classNames={{
              contentClassName:
                'CertsConsultedActionPopover-mobileDrawer--content',
            }}
          />
        </>
      ) : (
        <Popover
          isPortalEnabled
          placement={placement}
          trigger={trigger}
          popoverContent={
            <CertsConsultedContent
              isCommentPopoverOpen={isCommentPopoverOpen}
              setIsCommentPopoverOpen={setIsCommentPopoverOpen}
              onClose={onClose}
              resetAction={resetAction}
              certKey={certKey}
              nodeData={nodeData}
              globalDateFormat={globalDateFormat}
            />
          }
          isOpen={isCommentPopoverOpen}
          setIsOpen={setIsCommentPopoverOpen}
          classNames={{
            wrapperClassName:
              'CertsConsultedActionPopover-contextWrapper--popoverWrapper',
          }}
        />
      )}
    </Box>
  );
}

CertsConsultedActionPopover.propTypes = {
  isMobileXlargeWidth: PropTypes.bool.isRequired,
  isCommentPopoverOpen: PropTypes.bool.isRequired,
  setIsCommentPopoverOpen: PropTypes.func.isRequired,
  placement: PropTypes.string.isRequired,
  trigger: PropTypes.element.isRequired,
  onClose: PropTypes.func.isRequired,
  resetAction: PropTypes.func.isRequired,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  certKey: PropTypes.string.isRequired,
  globalDateFormat: PropTypes.string.isRequired,
};

export default CertsConsultedActionPopover;
